import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  CalendarRange,
  ChartColumn,
  Clock,
  Frown,
  Hourglass,
  OctagonAlert,
  RefreshCw,
  Search,
  ShieldAlert,
  Star,
  X,
} from "lucide-react";
import { useAnalysisOverview } from "../hooks/useAnalysisOverview";
import { useConversationsApi } from "../api/conversationsApi";
import FocusPanel from "../components/analysis/FocusPanel";
import PriorityCard from "../components/analysis/PriorityCard";
import AnalysisTrend from "../components/analysis/AnalysisTrend";
import ConversationTranscriptModal from "../modal/ConversationTranscriptModal";
import { formatScore, scoreColor } from "../components/analysis/analysisLabels";
import { formatDate } from "../utils/helpers";

const PERIODS = [
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
  { value: 180, label: "6 meses" },
  { value: 365, label: "1 ano" },
];

const isoDay = (date) => date.toISOString().slice(0, 10);

// Um predicado por visão: cada cartão do topo aponta para uma destas chaves, de
// modo que o número exibido e a lista filtrada saiam sempre da mesma regra.
const VIEWS = {
  critico: (item) => item.priority.level === "critico",
  atencao: (item) => item.priority.level === "atencao",
  estavel: (item) => item.priority.level === "estavel",
  insatisfeito: (item) => item.analysis.customerSatisfaction === "insatisfeito",
  semResolucao: (item) =>
    ["pendente", "parcial"].includes(item.analysis.resolutionStatus),
  notaBaixa: (item) =>
    typeof item.analysis.agentScore === "number" && item.analysis.agentScore <= 6,
  todas: () => true,
};

const SORTS = {
  priority: {
    label: "Prioridade",
    compare: (a, b) => b.priority.score - a.priority.score,
  },
  recent: {
    label: "Mais recentes",
    compare: (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt),
  },
  agentScore: {
    label: "Pior atendimento",
    compare: (a, b) =>
      (a.analysis.agentScore ?? 11) - (b.analysis.agentScore ?? 11),
  },
};

const KpiCard = ({ icon: Icon, tone, value, label, hint, alert, active, onClick }) => {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      onClick={onClick}
      title={onClick ? `Filtrar por ${label.toLowerCase()}` : undefined}
      className={`rounded-lg border bg-white p-4 text-left shadow-sm transition-colors dark:bg-gray-900 ${
        onClick ? "cursor-pointer hover:border-gray-400 dark:hover:border-gray-500" : ""
      } ${
        active
          ? "border-green-500 ring-2 ring-green-500/40 dark:border-green-500"
          : alert
            ? "border-red-300 dark:border-red-800"
            : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="mb-1 flex items-center justify-between">
        <Icon className={`h-5 w-5 ${tone}`} />
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {value}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      {hint && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </Wrapper>
  );
};

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
    }`}
  >
    {children}
  </button>
);

const AnalysisDashboard = ({ onOpenConversation }) => {
  const [days, setDays] = useState(30);
  // Intervalo de calendário: só entra em vigor quando as duas datas existem.
  const [range, setRange] = useState({ from: "", to: "" });
  const [customOpen, setCustomOpen] = useState(false);
  const custom = Boolean(range.from && range.to);
  const [view, setView] = useState("critico");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("priority");
  const [focusFilter, setFocusFilter] = useState(null);
  const [viewedItem, setViewedItem] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const { overview, loading, error, refetch } = useAnalysisOverview(
    days,
    custom ? range.from : null,
    custom ? range.to : null,
  );
  const api = useConversationsApi();

  const handleReanalyze = useCallback(
    async (conversationId) => {
      setReanalyzingId(conversationId);
      try {
        await api.summarize(conversationId, true);
        await refetch();
        toast.success("Análise atualizada.");
      } catch (err) {
        toast.error(err.message || "Não foi possível reanalisar a conversa.");
      } finally {
        setReanalyzingId(null);
      }
    },
    [api, refetch],
  );

  const handleToggleAutoAnalysis = useCallback(
    async (conversationId, enabled) => {
      setTogglingId(conversationId);
      try {
        await api.setAutoAnalysis(conversationId, enabled);
        await refetch();
        toast.success(
          enabled
            ? "Análise automática retomada para este cliente."
            : "Análise automática pausada para este cliente.",
        );
      } catch (err) {
        toast.error(err.message || "Não foi possível alterar a análise automática.");
      } finally {
        setTogglingId(null);
      }
    },
    [api, refetch],
  );

  const viewItems = useMemo(
    () => (overview?.items || []).filter(VIEWS[view] || VIEWS.todas),
    [overview, view],
  );

  const items = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = viewItems.filter((item) => {
      if (focusFilter && !focusFilter.ids.has(item.conversationId)) return false;
      if (!term) return true;
      return [
        item.contact?.name,
        item.contact?.phoneNumber,
        item.analysis.summary,
        ...(item.analysis.keyTopics || []),
        ...(item.analysis.dissatisfactionSignals || []),
        ...(item.analysis.recommendedActions || []),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term));
    });
    return [...filtered].sort(SORTS[sort].compare);
  }, [viewItems, search, sort, focusFilter]);

  // Clicar no painel de foco mostra exatamente as conversas por trás do número.
  // Filtramos pelos ids que o backend agrupou — o rótulo é uma categoria, não um
  // texto que apareça literalmente na conversa.
  const handleFocusSelect = useCallback((entry) => {
    setFocusFilter({
      label: entry.label,
      ids: new Set(entry.conversationIds || []),
    });
    setView("todas");
    setSearch("");
  }, []);

  if (loading && !overview) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Carregando análises...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-red-500">Erro ao carregar análises: {error}</p>
      </div>
    );
  }

  if (!overview) return null;

  const { stats, coverage, focus, agents, trend } = overview;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Análise de Atendimentos
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {coverage.analyzed} de {coverage.totalConversations} conversas com
              atividade {overview.period.custom
                ? `entre ${formatDate(overview.period.since)} e ${formatDate(
                    overview.period.until,
                  )}`
                : `nos últimos ${overview.period.days} dias`}{" "}
              já analisadas · {coverage.pending} aguardando análise
              {coverage.paused > 0 &&
                ` · ${coverage.paused} com automática pausada`}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => {
                    setDays(period.value);
                    setRange({ from: "", to: "" });
                    setCustomOpen(false);
                    setFocusFilter(null);
                  }}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                    !custom && days === period.value
                      ? "bg-green-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  {period.label}
                </button>
              ))}

              <button
                onClick={() => setCustomOpen((open) => !open)}
                className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  custom
                    ? "bg-green-500 text-white"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                }`}
                title="Escolher um intervalo no calendário"
              >
                <CalendarRange className="h-4 w-4" />
                Período
              </button>
            </div>
            <button
              onClick={refetch}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white transition-colors hover:bg-green-600 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
        </div>

        {customOpen && (
          <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <label className="text-sm text-gray-700 dark:text-gray-300">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                De
              </span>
              <input
                type="date"
                value={range.from}
                max={range.to || isoDay(new Date())}
                onChange={(event) => {
                  setRange((prev) => ({ ...prev, from: event.target.value }));
                  setFocusFilter(null);
                }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Até
              </span>
              <input
                type="date"
                value={range.to}
                min={range.from}
                max={isoDay(new Date())}
                onChange={(event) => {
                  setRange((prev) => ({ ...prev, to: event.target.value }));
                  setFocusFilter(null);
                }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </label>

            {custom ? (
              <button
                onClick={() => {
                  setRange({ from: "", to: "" });
                  setFocusFilter(null);
                }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Limpar intervalo
              </button>
            ) : (
              <p className="py-2 text-sm text-gray-500 dark:text-gray-400">
                Escolha as duas datas para aplicar o intervalo.
              </p>
            )}
          </div>
        )}

        {coverage.pending > coverage.analyzed && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
            <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              A fila de análise ainda está sendo processada:{" "}
              <span className="font-semibold">{coverage.pending}</span> das{" "}
              {coverage.totalConversations} conversas do período aguardam a IA. O
              painel mostra apenas as{" "}
              <span className="font-semibold">{coverage.analyzed}</span> já
              analisadas — a rotina automática processa as mais recentes primeiro
              e o quadro se completa conforme ela avança.
            </p>
          </div>
        )}

        {stats.criticalCount > 0 && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <OctagonAlert className="mt-0.5 h-6 w-6 shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-200">
                {stats.criticalCount} conversa
                {stats.criticalCount > 1 ? "s exigem" : " exige"} ação imediata
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Combinam risco alto, cliente insatisfeito e atendimento sem
                resolução. Comece pela fila de prioridade abaixo — ela está
                ordenada pelo impacto de cada caso.
              </p>
            </div>
          </div>
        )}

        <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <KpiCard
            icon={OctagonAlert}
            tone="text-red-500"
            value={stats.criticalCount}
            label="Aja agora"
            hint="Prioridade 60 ou mais"
            alert={stats.criticalCount > 0}
            active={view === "critico"}
            onClick={() => setView("critico")}
          />
          <KpiCard
            icon={ShieldAlert}
            tone="text-amber-500"
            value={stats.attentionCount}
            label="Atenção"
            hint="Prioridade 35 a 59"
            active={view === "atencao"}
            onClick={() => setView("atencao")}
          />
          <KpiCard
            icon={Frown}
            tone="text-orange-500"
            value={stats.dissatisfiedCount}
            label="Clientes insatisfeitos"
            hint={`${(stats.byRisk.critico || 0) + (stats.byRisk.alto || 0)} em risco alto`}
            active={view === "insatisfeito"}
            onClick={() => setView("insatisfeito")}
          />
          <KpiCard
            icon={Clock}
            tone="text-blue-500"
            value={stats.unresolvedCount}
            label="Sem resolução"
            hint="Pendentes ou parciais"
            active={view === "semResolucao"}
            onClick={() => setView("semResolucao")}
          />
          <KpiCard
            icon={Star}
            tone="text-purple-500"
            value={
              <span className={scoreColor(stats.avgAgentScore)}>
                {formatScore(stats.avgAgentScore)}
              </span>
            }
            label="Nota do atendimento"
            hint={`${stats.lowAgentScoreCount} com nota 6 ou menos`}
            active={view === "notaBaixa"}
            onClick={() => setView("notaBaixa")}
          />
          <KpiCard
            icon={ChartColumn}
            tone="text-green-500"
            value={coverage.analyzed}
            label="Conversas analisadas"
            hint={`de ${coverage.totalConversations} no período`}
            active={view === "todas"}
            onClick={() => setView("todas")}
          />
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Chip active={view === "todas"} onClick={() => setView("todas")}>
            Ver todas ({overview.items.length})
          </Chip>
          <Chip active={view === "estavel"} onClick={() => setView("estavel")}>
            Estáveis
          </Chip>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Os cartões acima filtram a fila; a nota média filtra as conversas com
            6 ou menos.
          </span>
        </div>

        <AnalysisTrend trend={trend} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  aria-label="Ordenar fila"
                >
                  {Object.entries(SORTS).map(([value, option]) => (
                    <option key={value} value={value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="relative w-56">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cliente, assunto, sinal..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-2 rounded p-0.5 text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
                      title="Limpar busca"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {items.length} de {overview.items.length} conversas na fila
              </p>

              {focusFilter && (
                <button
                  onClick={() => setFocusFilter(null)}
                  className="flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 transition-colors hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                  title="Remover filtro"
                >
                  {focusFilter.label}
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="space-y-4">
              {items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
                  <p className="text-gray-600 dark:text-gray-400">
                    {viewItems.length === 0
                      ? "Nenhuma conversa nesta seleção — bom sinal."
                      : "Nenhuma conversa corresponde aos filtros."}
                  </p>
                </div>
              ) : (
                items.map((item) => (
                  <PriorityCard
                    key={item.conversationId}
                    item={item}
                    onViewConversation={setViewedItem}
                    onOpenConversation={onOpenConversation}
                    onReanalyze={handleReanalyze}
                    onToggleAutoAnalysis={handleToggleAutoAnalysis}
                    reanalyzing={reanalyzingId === item.conversationId}
                    togglingAutoAnalysis={togglingId === item.conversationId}
                  />
                ))
              )}
            </div>

            {coverage.itemsShown < coverage.analyzed && (
              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                A fila mostra as {coverage.itemsShown} conversas de maior
                prioridade das {coverage.analyzed} analisadas no período — as
                críticas e as de atenção estão todas aqui. Os indicadores e o
                painel lateral consideram o período inteiro.
              </p>
            )}

            {coverage.scanTruncated && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                O período excedeu o limite de varredura do servidor. Estreite o
                intervalo para que os indicadores cubram tudo.
              </p>
            )}
          </div>

          <FocusPanel
            focus={focus}
            agents={agents}
            onSelect={handleFocusSelect}
          />
        </div>
      </div>

      {viewedItem && (
        <ConversationTranscriptModal
          item={viewedItem}
          onClose={() => setViewedItem(null)}
          onOpenFull={
            onOpenConversation &&
            ((conversationId) => {
              setViewedItem(null);
              onOpenConversation(conversationId);
            })
          }
        />
      )}
    </div>
  );
};

export default AnalysisDashboard;
